import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createRoute, redirect } from '@tanstack/react-router';
import { useGT } from 'gt-react';
import { z } from 'zod';
import type { McpAuthorizationRequest } from '@erp/rpc';

import { orpc } from 'src/api/rpc/client';
import { Spinner } from 'src/components/ui/spinner';
import { Route as rootRoute } from 'src/pages/__root';
import { AuthShell } from 'src/pages/(unauthenticated)/AuthShell';
import { ConsentDenied } from './components/ConsentDenied';
import { ConsentPrompt } from './components/ConsentPrompt';
import { InvalidRequest } from './components/InvalidRequest';
import { decodeConsentRequest } from './components/consentRequest';

/**
 * Consent screen of the Gently MCP OAuth flow. The backend's
 * /api/v1/mcp/oauth/authorize validates the request and forwards its query
 * string here; approving mints an authorization code and sends the browser
 * back to the AI client. See docs/mcp/auth.md.
 */
export const Route = createRoute({
  path: '/oauth/mcp/authorize',
  getParentRoute: () => rootRoute,
  validateSearch: z.object({ request: z.string().optional() }),
  beforeLoad: ({ context, location }) => {
    if (!context.auth.currentUser) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: McpAuthorizePage,
});

/**
 * Never render consent inside a frame: a clickjacking page could otherwise
 * trick a user into approving an attacker's client.
 */
function McpAuthorizePage() {
  const gt = useGT();
  const request = decodeConsentRequest(Route.useSearch().request);
  if (window.self !== window.top) {
    return (
      <InvalidRequest
        error={gt(
          "This page can't be shown inside another site. Open it in its own tab.",
          { context: 'Gently MCP consent: page was loaded inside an iframe' },
        )}
      />
    );
  }
  if (!request) {
    return (
      <InvalidRequest
        error={gt('This authorization link is incomplete or damaged.', {
          context:
            'Gently MCP consent: the forwarded request could not be read',
        })}
      />
    );
  }
  return <McpConsent request={request} />;
}

function McpConsent({ request }: { request: McpAuthorizationRequest }) {
  const description = useQuery({
    ...orpc.mcpOAuth.describeAuthorization.queryOptions({ input: request }),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const data = description.data;
  useEffect(() => {
    if (data?.status === 'redirect') {
      window.location.assign(data.redirectUrl);
    }
  }, [data]);

  if (description.isError) {
    return <InvalidRequest error={description.error} />;
  }
  if (!data || data.status === 'redirect') {
    return (
      <AuthShell washOut>
        <div className="flex justify-center py-6">
          <Spinner size="xl" />
        </div>
      </AuthShell>
    );
  }
  if (data.status === 'invalid') {
    return <InvalidRequest error={data.description} />;
  }
  const clientName = data.clientName ?? data.redirectTarget;
  return data.status === 'denied' ? (
    <ConsentDenied
      request={request}
      clientName={clientName}
      reason={data.reason}
    />
  ) : (
    <ConsentPrompt
      request={request}
      clientName={clientName}
      description={data}
    />
  );
}
